import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface UserDetail {
  id: string;
  email: string;
  full_name: string | null;
  plan: string | null;
  total_credits: number | null;
  monthly_images_used: number;
  monthly_scripts_used: number;
  monthly_audio_minutes_used: number;
  daily_generation_limit: number;
  is_banned: boolean;
  banned_at: string | null;
  banned_reason: string | null;
  notes: string | null;
  created_at: string;
  last_active: string | null;
  role?: string;
}

export interface ActivityLog {
  id: string;
  activity_type: string;
  description: string | null;
  metadata: any;
  created_at: string;
}

export interface AdminAction {
  id: string;
  admin_user_id: string;
  action_type: string;
  details: any;
  created_at: string;
  admin_email?: string;
}

export const useAdminUserDetails = (userId: string | undefined) => {
  const [user, setUser] = useState<UserDetail | null>(null);
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [adminActions, setAdminActions] = useState<AdminAction[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchUserDetails = async () => {
    if (!userId) return;

    try {
      setLoading(true);

      // Fetch user profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (profileError) throw profileError;

      // Fetch user role
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .limit(1)
        .single();

      // Fetch activity logs
      const { data: logs } = await supabase
        .from('user_activity_logs')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      // Fetch admin actions on this user
      const { data: actions } = await supabase
        .from('admin_actions')
        .select('*, admin:admin_user_id(email)')
        .eq('target_user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      setUser({
        id: profile.user_id,
        email: profile.email,
        full_name: profile.full_name,
        plan: profile.plan,
        total_credits: profile.total_credits,
        monthly_images_used: profile.monthly_images_used,
        monthly_scripts_used: profile.monthly_scripts_used,
        monthly_audio_minutes_used: profile.monthly_audio_minutes_used,
        daily_generation_limit: profile.daily_generation_limit,
        is_banned: profile.is_banned,
        banned_at: profile.banned_at,
        banned_reason: profile.banned_reason,
        notes: profile.notes,
        created_at: profile.created_at,
        last_active: profile.last_active,
        role: roleData?.role || 'user'
      });

      setActivityLogs(logs || []);
      setAdminActions(actions || []);
    } catch (error) {
      console.error('Error fetching user details:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch user details',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateUserPlan = async (newPlan: string) => {
    if (!userId) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ plan: newPlan })
        .eq('user_id', userId);

      if (error) throw error;

      // Log subscription change
      await supabase.from('subscription_history').insert({
        user_id: userId,
        action: 'plan_changed',
        old_plan: user?.plan,
        new_plan: newPlan,
        performed_by: (await supabase.auth.getUser()).data.user?.id
      });

      await supabase.rpc('log_admin_action_detailed', {
        p_action_type: 'update_plan',
        p_target_user_id: userId,
        p_details: { old_plan: user?.plan, new_plan: newPlan }
      });

      toast({
        title: 'Success',
        description: 'User plan updated successfully'
      });

      fetchUserDetails();
    } catch (error) {
      console.error('Error updating plan:', error);
      toast({
        title: 'Error',
        description: 'Failed to update user plan',
        variant: 'destructive'
      });
    }
  };

  const updateUserCredits = async (credits: number) => {
    if (!userId) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ total_credits: credits })
        .eq('user_id', userId);

      if (error) throw error;

      await supabase.rpc('log_admin_action_detailed', {
        p_action_type: 'update_credits',
        p_target_user_id: userId,
        p_details: { old_credits: user?.total_credits, new_credits: credits }
      });

      toast({
        title: 'Success',
        description: 'User credits updated successfully'
      });

      fetchUserDetails();
    } catch (error) {
      console.error('Error updating credits:', error);
      toast({
        title: 'Error',
        description: 'Failed to update user credits',
        variant: 'destructive'
      });
    }
  };

  const updateDailyLimit = async (limit: number) => {
    if (!userId) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ daily_generation_limit: limit })
        .eq('user_id', userId);

      if (error) throw error;

      await supabase.rpc('log_admin_action_detailed', {
        p_action_type: 'update_daily_limit',
        p_target_user_id: userId,
        p_details: { new_limit: limit }
      });

      toast({
        title: 'Success',
        description: 'Daily generation limit updated successfully'
      });

      fetchUserDetails();
    } catch (error) {
      console.error('Error updating limit:', error);
      toast({
        title: 'Error',
        description: 'Failed to update daily limit',
        variant: 'destructive'
      });
    }
  };

  const banUser = async (reason: string) => {
    if (!userId) return;

    try {
      await supabase.rpc('ban_user', {
        p_user_id: userId,
        p_reason: reason
      });

      toast({
        title: 'Success',
        description: 'User banned successfully'
      });

      fetchUserDetails();
    } catch (error) {
      console.error('Error banning user:', error);
      toast({
        title: 'Error',
        description: 'Failed to ban user',
        variant: 'destructive'
      });
    }
  };

  const unbanUser = async () => {
    if (!userId) return;

    try {
      await supabase.rpc('unban_user', {
        p_user_id: userId
      });

      toast({
        title: 'Success',
        description: 'User unbanned successfully'
      });

      fetchUserDetails();
    } catch (error) {
      console.error('Error unbanning user:', error);
      toast({
        title: 'Error',
        description: 'Failed to unban user',
        variant: 'destructive'
      });
    }
  };

  const resetUsage = async () => {
    if (!userId) return;

    try {
      await supabase.rpc('reset_user_usage', {
        p_user_id: userId
      });

      toast({
        title: 'Success',
        description: 'User usage statistics reset successfully'
      });

      fetchUserDetails();
    } catch (error) {
      console.error('Error resetting usage:', error);
      toast({
        title: 'Error',
        description: 'Failed to reset usage statistics',
        variant: 'destructive'
      });
    }
  };

  const updateNotes = async (notes: string) => {
    if (!userId) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ notes })
        .eq('user_id', userId);

      if (error) throw error;

      await supabase.rpc('log_admin_action_detailed', {
        p_action_type: 'update_notes',
        p_target_user_id: userId
      });

      toast({
        title: 'Success',
        description: 'Notes updated successfully'
      });

      fetchUserDetails();
    } catch (error) {
      console.error('Error updating notes:', error);
      toast({
        title: 'Error',
        description: 'Failed to update notes',
        variant: 'destructive'
      });
    }
  };

  const deleteUser = async () => {
    if (!userId) return;

    try {
      // Delete user profile
      const { error } = await supabase
        .from('profiles')
        .delete()
        .eq('user_id', userId);

      if (error) throw error;

      await supabase.rpc('log_admin_action_detailed', {
        p_action_type: 'delete_user',
        p_target_user_id: userId
      });

      toast({
        title: 'Success',
        description: 'User deleted successfully'
      });
    } catch (error) {
      console.error('Error deleting user:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete user',
        variant: 'destructive'
      });
    }
  };

  useEffect(() => {
    if (userId) {
      fetchUserDetails();

      // Real-time subscription
      const channel = supabase
        .channel(`user-details-${userId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'profiles',
            filter: `user_id=eq.${userId}`
          },
          () => {
            fetchUserDetails();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [userId]);

  return {
    user,
    activityLogs,
    adminActions,
    loading,
    updateUserPlan,
    updateUserCredits,
    updateDailyLimit,
    banUser,
    unbanUser,
    resetUsage,
    updateNotes,
    deleteUser,
    refresh: fetchUserDetails
  };
};
